<script lang="ts" setup>
    import '../../../css/choice.css';
    import * as ChoiceApi from "../../lib/ChoiceApi"
    import { onMounted, ref, reactive, computed } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { ICol, IRowSelection, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import { ButtonDisplaySize, TableRowHighlight, TableRowType, IconTypes, ButtonDisplayStyle, DisplayStyle } from "../../Enums/UiEnums";
    import { IntakeAssignResultItem } from '../../lib/Models/IntakeModel';
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { popAlert } from '../../lib/Notification';     
    import ResizeMonitor from '../../components/ResizeMonitor.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import Icon from '../../components/Icon.vue'
    import Row from "../../components/Section/Row.vue"

    const privilegesStore = usePrivilegesStore(initPinia());

    const props = defineProps<{
        resetList: IntakeAssignResultItem[],
    }>(
    );

    const formData = reactive<{
        resetList: IntakeAssignResultItem[],
        windowHeight: number,
        currentRow?: IntakeAssignResultItem,
    }>({
        resetList: [],
        windowHeight: 100,
    });

    function PassFail(value: any, _col: ICol, _row: Record<string, any>): string | undefined
    {
        return `${_row?.workCount ?? 0} / ${_row?.errorCount ?? 0}`;
    }

    const keyColumns = ref<ICol[]>([
        { Name: 'Appointment Type', DataField: 'appointmentTypeDesc' },
        { Name: 'New Intake', DataField: 'newIntakeSetupName' },
        { Name: 'Add Missing', DataField: 'addMissing', ExtraData: "AddMissing" },
        { Name: 'Update Intakes', DataField: 'updateIntake', ExtraData: "UpdateIntakes" },
        { Name: 'Status', DataField: 'statusDesc', ExtraData: "Status" },
        { Name: 'Pass/Fail', DataField: 'workCount', FormatBodyValue: PassFail },
        { Name: 'Completion', DataField: 'completionPercentage', RenderMode: RenderModeType.Percent },
        { Name: 'Posted', DataField: 'postedDate' },
        { Name: 'User', DataField: 'usersId' },
    ]);

    onMounted(() => {
        formData.resetList = props.resetList;
        windowResize();
    });

    function windowResize() {
        formData.windowHeight = window.innerHeight - 70;
    }

    const deleteButtonDisabled = computed<boolean>(() => {
        if (!formData.currentRow)
            return true;
        if (formData.currentRow.status != 0)
            return true;
        return false;
    });

    function onRowClick(rowData: IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        formData.currentRow = <IntakeAssignResultItem> rowData;
    }

    async function onDelete() {
        if (!formData.currentRow)
            return;
        const result = await ChoiceApi.Intake.ResetDelete(formData.currentRow.intakeResetId);
        if (!result) {
            await popAlert("There was an error deleting the Intake Reset", "Intake Reset Delete");
            return;
        }
        formData.resetList = result;
    }

    async function onRefresh() {
        const result = await ChoiceApi.Intake.ResetRefresh();
        if (!result) {
            await popAlert("There was an error refreshing the Intake Reset", "Intake Reset Refresh");
            return;
        }
        formData.resetList = result;
    }
</script>

<template>
    <ResizeMonitor @resize="windowResize" />
    <Row :topPadding="2">
        <ChoiceTable 
            ref="keyTable"
            :columns="keyColumns" 
            :data="formData.resetList" 
            :show-filters="false" 
            :allowSorting="false"
            :height="formData.windowHeight"
            fontSize="smaller"
            :rowHighlight="TableRowHighlight.onSingleClick"
            :showPaginationButtonsBelowChoiceTable="true"
            @rowclick="onRowClick"
            :key-value-data-member="'intakeResetId'">
            <template #cell="{col, row, value}">
                <div v-if="'AddMissing' == col.ExtraData" class="d-flex justify-content-center">
                    <Icon v-if="value" :icon="IconTypes.Ready" :displayStyle="DisplayStyle.Dark"/>
                </div>
                <div v-if="'UpdateIntakes' == col.ExtraData" class="d-flex justify-content-center">
                    <Icon v-if="value" :icon="IconTypes.Ready" :displayStyle="DisplayStyle.Dark"/>
                </div>
                <div v-if="'Status' == col.ExtraData">
                    <Icon v-if="value == 'Ready to Process'" :icon="IconTypes.Waiting" :displayStyle="DisplayStyle.Dark" :endingPadding="1"/>
                    <Icon v-if="value == 'Processing'" :icon="IconTypes.Processing" :displayStyle="DisplayStyle.Primary" :endingPadding="1"/>
                    <Icon v-if="value == 'Completed'" :icon="IconTypes.Complete" :displayStyle="DisplayStyle.Success" :endingPadding="1"/>
                    <Icon v-if="value == 'Deleted'" :icon="IconTypes.Deleted" :displayStyle="DisplayStyle.Danger" :endingPadding="1"/>
                    <Icon v-if="value == 'Error'" :icon="IconTypes.Error" :displayStyle="DisplayStyle.Warning" :endingPadding="1"/>
                    {{value}}
                </div>
            </template>
        </ChoiceTable>
    </Row>
    <Row>
        <ActionButtonV2 
            :action="ActionButtonStyle.Refresh" 
            text="Refresh"
            spinText="Refreshing"
            tooltip="Refresh the Intake Reset list"
            :size="ButtonDisplaySize.Small" 
            :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
            :performAction="onRefresh"
            :endingPadding="1" />                
        <ActionButtonV2 
            :action="ActionButtonStyle.Delete" 
            text="Delete"
            spinText="Deleting"
            tooltip="Delete the intake reset"
            :disabled="deleteButtonDisabled"
            :size="ButtonDisplaySize.Small" 
            :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
            :performAction="onDelete" />                
    </Row>
    <ChoiceStateMessageSink />
</template>
